import { Table, Pagination } from 'antd';
import { useEffect, useState } from 'react';

// Define interfaces for props and data
interface TableRecord {
  [key: string]: any;
}

interface GeneralCustomTableProps {
  tableHeadList: any[]; // Replace with specific column type from antd if needed
  tableDataList: TableRecord[];
  handleRowClick?: (record: TableRecord) => void;
  rowKey: string | ((record: TableRecord) => string);
  setSelectedRows?: (rows: TableRecord[]) => void;
  allowCheckBox?: boolean;
  tableSummary?: (currentData: readonly TableRecord[]) => React.ReactNode;
  pageSizeOptions?: Array<string | number>;
}

function GeneralCustomTable({
  tableHeadList,
  tableDataList,
  handleRowClick,
  rowKey,
  setSelectedRows,
  allowCheckBox,
  tableSummary,
  pageSizeOptions,
}: GeneralCustomTableProps) {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  // Reset to page 1 when data changes
  useEffect(() => {
    setCurrentPage(1);
  }, [tableDataList]);

  // Compute paginated data
  const paginatedData = tableDataList.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys: React.Key[], rows: TableRecord[]) => {
      setSelectedRowKeys(keys);
      setSelectedRows?.(rows);
    },
  };

  const handlePaginationChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const paginationOptions = {
    current: currentPage,
    pageSize: pageSize,
    showSizeChanger: true,
    total: tableDataList?.length || 0,
    onChange: handlePaginationChange,
    onShowSizeChange: handlePaginationChange, // Reset page on size change
    pageSizeOptions: pageSizeOptions || ['10', '20', '50', '100'],
    showTotal: (total: number) => `Showing ${paginatedData.length} of ${total} items`,
    className: 'flex justify-end items-center space-x-2',
  };

  return (
    <div className="overflow-x-auto bg-white rounded-lg shadow-md border border-gray-200">
      <Table
        columns={tableHeadList}
        dataSource={paginatedData} // Now correctly paginated!
        rowKey={typeof rowKey === 'function' ? rowKey : (record: TableRecord) => record[rowKey]}
        pagination={false}
        summary={tableSummary}
        rowSelection={allowCheckBox ? rowSelection : undefined}
        rowClassName={(record, index) =>
          `cursor-pointer hover:bg-gray-100 transition-colors duration-200 ${
            index % 2 === 0 ? 'bg-gray-50' : 'bg-white'
          }`
        }
        onRow={(record: TableRecord) => ({
          onClick: () => handleRowClick?.(record),
        })}
        locale={{ emptyText: <div className="py-10 text-gray-500">No data available</div> }}
        className="
          [&_.ant-table-thead>tr>th]:bg-gray-200 
          [&_.ant-table-thead>tr>th]:text-sm 
          [&_.ant-table-thead>tr>th]:font-semibold 
          [&_.ant-table-thead>tr>th]:text-gray-900
          [&_.ant-table-thead>tr>th]:px-4 
          [&_.ant-table-thead>tr>th]:py-3
          [&_.ant-table-tbody>tr>td]:text-sm 
          [&_.ant-table-tbody>tr>td]:text-gray-700 
          [&_.ant-table-tbody>tr>td]:px-4 
          [&_.ant-table-tbody>tr>td]:py-3
          [&_.ant-table-cell]:border-b 
          [&_.ant-table-cell]:border-gray-200
        "
        scroll={{ x: 'max-content' }}
      />
      {tableDataList?.length > 0 && (
        <div className="p-4 border-t border-gray-200 flex justify-between items-center">
          <div className="text-sm text-gray-600">
            Showing {(currentPage - 1) * pageSize + 1} -{' '}
            {Math.min(currentPage * pageSize, tableDataList.length)} of {tableDataList.length}
          </div>
          <Pagination {...paginationOptions} />
        </div>
      )}
    </div>
  );
}

export default GeneralCustomTable;
